import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import conversationService, { 
  ConversationMessage, 
  SendMessageRequest 
} from '../../services/conversationService';
import { Chart as ChartJS, registerables } from 'chart.js';
import { processMarkdown } from '../../utils/aiResponseParser';
import './FinancialDataChatbot.css';

// Register Chart.js components including Colors plugin
ChartJS.register(...registerables);

// Chart data interface for the chatbot
interface ChatChartData {
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'scatter' | 'area';
  title: string;
  description?: string;
  labels: string[];
  datasets: {
    label: string;
    data: number[] | Array<{x: number, y: number}>;
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    fill?: boolean;
  }[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  indexAxis?: 'x' | 'y'; // Support for horizontal bar charts
}

interface ChatMessage extends ConversationMessage {
  isLoading?: boolean;
  error?: string;
  showNewConversationButton?: boolean;
  showSmartModeOffer?: boolean;
}

interface SmartModeState {
  enabled: boolean;
  aggressiveness: 'conservative' | 'balanced' | 'aggressive';
  settings: {
    autoTimeFiltering: boolean;
    autoKPIFiltering: boolean;
    focusNonZero: boolean;
    useConversationContext: boolean;
  };
  dataReduction?: {
    originalSize: number;
    filteredSize: number;
    percentage: number;
  };
}

interface FinancialDataChatbotProps {
  currentFilter?: any;
  currentDataCount?: number;
  isVisible: boolean;
  onClose: () => void;
}

const FinancialDataChatbot: React.FC<FinancialDataChatbotProps> = ({
  currentFilter,
  currentDataCount = 0,
  isVisible,
  onClose
}) => {
  console.log('🤖 CHATBOT COMPONENT LOADED/RENDERED');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showVisualizations, setShowVisualizations] = useState(true);
  const [hideCalculations, setHideCalculations] = useState(true);
  
  // Smart Mode state
  const [smartMode, setSmartMode] = useState<SmartModeState>({
    enabled: false,
    aggressiveness: 'balanced',
    settings: {
      autoTimeFiltering: true,
      autoKPIFiltering: true,
      focusNonZero: true,
      useConversationContext: false
    }
  });
  const [showDashboard, setShowDashboard] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardInput, setDashboardInput] = useState('');
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [focusedChart, setFocusedChart] = useState<ChatChartData | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Debug: Track re-renders
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`🔄 FinancialDataChatbot render #${renderCount.current}`, {
    isVisible,
    currentDataCount,
    dashboardInput: dashboardInput.length,
    showDashboard,
    messagesLength: messages.length,
    conversationId: conversationId
  });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Cache for parsed message content to prevent re-parsing and re-rendering old visualizations
  const parsedMessagesCache = useRef<Map<string, any>>(new Map());
  
  // Track if we've initialized the conversation context for this component instance
  const hasInitialized = useRef(false);

  // Initialize conversation context on component mount
  useEffect(() => {
    if (!hasInitialized.current) {
      console.log('🧹 INITIALIZING CONVERSATION CONTEXT');
      try {
        // Always clear on component mount (page reload/navigation)
        sessionStorage.removeItem('chatbot_conversationId');
        console.log('✅ Cleared stale conversation ID on mount');
        hasInitialized.current = true;
      } catch (error) {
        console.warn('❌ Failed to clear conversation ID on mount:', error);
      }
    }
  }, []);

  // Note: We don't need complex restoration logic since sendMessage always checks sessionStorage directly

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chatbot becomes visible
  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  // Handle keyboard shortcuts for fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key to exit fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
      // F11 or Ctrl+Shift+F to toggle fullscreen
      if ((e.key === 'F11' || (e.ctrlKey && e.shiftKey && e.key === 'F')) && isVisible) {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, isFullscreen]);

  // Generate a unique message ID
  const generateMessageId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Advanced AI response parser - extracts charts and questions with improved accuracy (memoized)
  const parseAIResponse = useCallback((content: string) => {
         console.log('🔍 Parsing AI response:', content.substring(0, 200) + '...');
    
    // Enhanced chart extraction with multiple patterns
    const chartPatterns = [
      /```chart\s*([\s\S]*?)\s*```/gi,
      /```json\s*({[\s\S]*?"type"[\s\S]*?})\s*```/gi,
      /\{[\s\S]*?"type":\s*["'][^"']*["'][\s\S]*?\}/gi
    ];
    
    let chartData = null;
    let contentWithoutChart = content;
    
    // Try each pattern to find chart data
    for (const pattern of chartPatterns) {
      const matches = Array.from(content.matchAll(pattern));
      if (matches.length > 0) {
        const match = matches[0];
        try {
          const jsonStr = match[1] || match[0];
          console.log('🔍 Attempting to parse JSON:', jsonStr);
          const parsed = JSON.parse(jsonStr.trim());
          console.log('✅ JSON parsed successfully:', parsed);
          
          // Validate it looks like chart data
          if (parsed.type && (parsed.labels || parsed.datasets || parsed.data)) {
            // Transform old scatter plot format to new format
            if (parsed.type === 'scatter' && parsed.data && !parsed.datasets) {
              console.log('🔄 Converting old scatter format to new format');
              chartData = {
                type: parsed.type,
                title: parsed.title,
                description: parsed.description,
                labels: [],
                datasets: [
                  {
                    label: parsed.title || 'Data Points',
                    data: parsed.data
                  }
                ],
                xAxisLabel: parsed.xAxisLabel,
                yAxisLabel: parsed.yAxisLabel
              };
            } else {
              chartData = parsed;
            }
            contentWithoutChart = content.replace(match[0], '').trim();
            console.log('📊 Chart data extracted:', chartData);
            break;
          } else {
            console.log('❌ Parsed JSON missing required fields (type and labels/datasets/data)');
          }
        } catch (error) {
          console.warn('⚠️ Chart parsing attempt failed:', error);
        }
      }
    }
    
              // More aggressive question extraction
     let recommendedQuestions: string[] = [];
     let mainContent = contentWithoutChart;
     
     // Enhanced question extraction to handle markdown headers and various formats
     const questionSectionPatterns = [
       // Markdown header format
       /#{1,6}\s*Recommended next questions[\s\S]*?:\s*([\s\S]*?)$/i,
       // Plain text format (original)
       /Recommended next questions based on the data and the last question:\s*([\s\S]*?)$/i,
       // Alternative markdown formats
       /\*\*Recommended next questions[\s\S]*?\*\*\s*([\s\S]*?)$/i,
               // Fallback - look for numbered list at the end
        /((?:\d+\.\s+[^\n]+\n?){3})\s*$/
     ];
     
     let questionMatch = null;
     for (const pattern of questionSectionPatterns) {
       questionMatch = contentWithoutChart.match(pattern);
       if (questionMatch) {
         console.log('🎯 Found questions using pattern:', pattern.source);
         break;
       }
     }
     
     if (questionMatch) {
       const questionsText = questionMatch[1];
       // Remove the entire questions section from main content
       mainContent = contentWithoutChart.replace(questionMatch[0], '').trim();
       
       console.log('🔍 Raw questions text found:', questionsText);
       
       // Extract numbered questions with more flexible parsing
       recommendedQuestions = questionsText
         .split(/\n/)
         .map(line => line.trim())
         .filter(line => line.length > 5)
         .filter(line => /^\d+\./.test(line)) // Must start with number and dot
         .map(line => line.replace(/^\d+\.\s*/, '').trim()) // Remove "1. " etc
         .map(line => line.replace(/\*\*/g, '')) // Remove markdown bold
         .map(line => line.replace(/\*/g, '')) // Remove markdown italic
         .filter(question => question.length > 10 && question.length < 400)
         .slice(0, 3);
       
       console.log('✅ Extracted questions:', recommendedQuestions);
     } else {
       // Fallback: try to find any numbered list in the content
       const fallbackQuestions = contentWithoutChart
         .split(/\n/)
         .map(line => line.trim())
         .filter(line => /^\d+\./.test(line))
         .map(line => line.replace(/^\d+\.\s*/, '').trim())
         .map(line => line.replace(/\*\*/g, '').replace(/\*/g, ''))
         .filter(question => question.length > 10 && question.length < 400)
         .slice(0, 3);
       
       if (fallbackQuestions.length > 0) {
         recommendedQuestions = fallbackQuestions;
         console.log('✅ Fallback questions extracted:', recommendedQuestions);
       } else {
         console.log('⚠️ No questions found in content');
       }
     }
    
    // Ensure chart data matches our interface and has valid content
    let processedChartData: ChatChartData | null = null;
    console.log('🔍 Processing chart data:', chartData);
    if (chartData && chartData.datasets && chartData.datasets.length > 0) {
             // Filter out datasets with no valid data
       const validDatasets = chartData.datasets.filter((dataset: any) => {
         return dataset && 
                dataset.data && 
                Array.isArray(dataset.data) && 
                dataset.data.length > 0 &&
                dataset.data.some((value: any) => 
                  (typeof value === 'number' && !isNaN(value)) ||
                  (typeof value === 'object' && value !== null && 'x' in value && 'y' in value)
                );
       });

      // Only create chart data if we have valid datasets
      if (validDatasets.length > 0) {
        processedChartData = {
          type: chartData.type || 'bar',
          title: chartData.title || 'Chart',
          description: chartData.description,
          labels: chartData.labels || [],
          datasets: validDatasets,
          xAxisLabel: chartData.xAxisLabel,
          yAxisLabel: chartData.yAxisLabel
        };
        
        console.log('📊 Processed valid chart data:', processedChartData);
      } else {
        console.log('🚫 No valid datasets found, skipping chart');
      }
    }

         console.log('🎯 Parsing complete - Questions:', recommendedQuestions.length, 'Chart:', !!processedChartData);

     return { 
       mainContent: mainContent.trim(), 
       recommendedQuestions, 
       chartData: processedChartData 
     };
  }, []);

  // Cached version of parseAIResponse to prevent re-parsing old messages
  const getCachedParsedResponse = useCallback((messageId: string, content: string) => {
    // Check if we already have this message parsed
    if (parsedMessagesCache.current.has(messageId)) {
      return parsedMessagesCache.current.get(messageId);
    }
    
    // Parse the message and cache the result
    const parsed = parseAIResponse(content);
    parsedMessagesCache.current.set(messageId, parsed);
    return parsed;
  }, [parseAIResponse]);


  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    console.log('🔄 Toggling fullscreen from', isFullscreen, 'to', !isFullscreen);
    setIsFullscreen(!isFullscreen);
  };

  // Toggle minimize mode
  const toggleMinimize = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    console.log('📦 Toggling minimize from', isMinimized, 'to', !isMinimized);
    console.log('📊 Dashboard state before minimize:', showDashboard);
    
    // Set minimizing flag to prevent dashboard from closing
    setIsMinimizing(true);
    
    // Use setTimeout to prevent any DOM manipulation conflicts
    setTimeout(() => {
      setIsMinimized(prev => {
        const newMinimized = !prev;
        console.log('📦 Minimize state changed to:', newMinimized);
        return newMinimized;
      });
      
      // Exit fullscreen when minimizing
      if (!isMinimized && isFullscreen) {
        setIsFullscreen(false);
      }
      
      // Clear minimizing flag after a short delay
      setTimeout(() => {
        setIsMinimizing(false);
      }, 100);
    }, 0);
  };

  // Smart Mode functions
  const toggleSmartMode = () => {
    console.log('🧠 Toggling smart mode from', smartMode.enabled, 'to', !smartMode.enabled);
    const newEnabled = !smartMode.enabled;
    setSmartMode(prev => ({ ...prev, enabled: newEnabled }));
    
    // Store user preference
    sessionStorage.setItem('smartModeEnabled', newEnabled.toString());
    
    // Add a message to inform user about the change
    if (newEnabled) {
      const smartModeMessage: ChatMessage = {
        id: generateMessageId(),
        conversationId: conversationId || 'system',
        role: 'assistant',
        content: `🧠 **Smart Mode Enabled**\n\nI'll now analyze your questions to focus on the most relevant data. This may increase response times but helps me provide more targeted insights for larger datasets.`,
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, smartModeMessage]);
    } else {
      const standardModeMessage: ChatMessage = {
        id: generateMessageId(),
        conversationId: conversationId || 'system',
        role: 'assistant',
        content: `🔍 **Standard Mode Enabled**\n\nI'll now use all available data without pre-filtering. Responses will be faster but may hit context limits with very large datasets.`,
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, standardModeMessage]);
    }
  };

  const enableSmartMode = () => {
    console.log('✨ Enabling smart mode');
    setSmartMode(prev => ({ ...prev, enabled: true }));
    sessionStorage.setItem('smartModeEnabled', 'true');
    
    // Clear conversation and start fresh with smart mode
    setConversationId(null);
    setMessages([]);
    setError(null);
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('chatbot_conversationId');
    } catch (error) {
      console.warn('Failed to clear conversationId from sessionStorage:', error);
    }
    
    // Add smart mode welcome message with performance warning
    const welcomeMessage: ChatMessage = {
      id: generateMessageId(),
      conversationId: 'welcome',
      role: 'assistant',
      content: `🧠 **Smart Mode Activated!**\n\nI'll now automatically focus on the most relevant data for your questions. This helps me:\n• Process larger datasets efficiently\n• Maintain longer conversations\n• Provide faster, more focused insights\n\n⏱️ **Performance Note**: Smart mode analyzes your questions to filter data intelligently, which may increase response times. This extra processing ensures you get the most relevant insights!\n\nI can see you have ${currentDataCount} records loaded. What would you like to analyze?`,
      createdAt: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
  };

    // Generate dynamic dashboard using AI
  const generateDashboard = useCallback(async (customMessage?: string) => {
    console.log('🎨 generateDashboard called with:', customMessage);
    setIsDashboardLoading(true);
    
    // If no custom message and no dashboard is open, just open the modal
    if (!customMessage && !showDashboard) {
      setShowDashboard(true);
      setIsDashboardLoading(false);
      return;
    }
    
    if (!showDashboard) {
      setShowDashboard(true);
    }
    
          try {
        const currentInputValue = dashboardInputRef.current?.value?.trim() || '';
        const dashboardMessage = customMessage || currentInputValue || 'Generate a comprehensive dashboard with multiple chart types showing key insights from this data. Include different visualization types like bar charts, line charts, pie charts, and scatter plots to tell the complete data story.';
      
      const request: SendMessageRequest = {
        message: dashboardMessage,
        conversationId: undefined, // Keep dashboard separate from main conversation
        requestType: 'DynamicDashboard',
        dataFilter: {
          ...currentFilter,
          limit: Math.min(currentDataCount, 1000) // More data for dashboard
        }
      };

              const response = await conversationService.sendMessage(request);
        console.log('🎨 AI Response received, length:', response.content?.length);
        
        if (response.content) {
          // Parse the dashboard response to extract multiple charts
          const dashboardCharts = parseDashboardResponse(response.content, dashboardMessage);
          console.log('🎨 Dashboard data set:', dashboardCharts);
          setDashboardData(dashboardCharts);
        } else {
          console.error('❌ No content in AI response');
        }
    } catch (error) {
      console.error('❌ Dashboard generation failed:', error);
      setDashboardData({ error: 'Failed to generate dashboard. Please try again.' });
          } finally {
        setIsDashboardLoading(false);
        // Clear input after generation
        if (dashboardInputRef.current && !customMessage) {
          dashboardInputRef.current.value = '';
        }
      }
     }, [showDashboard, currentFilter, currentDataCount]);

  // Handle dashboard button click
  const handleDashboardClick = useCallback(() => {
    generateDashboard();
  }, [generateDashboard]);

  // Handle dashboard input submission
  const handleDashboardSubmit = useCallback(() => {
    console.log('🚀 Dashboard submit triggered');
    const inputValue = dashboardInputRef.current?.value?.trim();
    console.log('📝 Input value:', inputValue);
    
    if (inputValue) {
      console.log('✅ Generating with custom input:', inputValue);
      setDashboardInput(inputValue); // Update state when submitting with custom input
      generateDashboard(inputValue);
    } else {
      console.log('🎯 Generating with default message');
      // Generate with default message if no custom input
      generateDashboard();
    }
  }, [generateDashboard]);

  // Handle dashboard retry
  const handleDashboardRetry = useCallback(() => {
    generateDashboard('Generate a comprehensive dashboard with multiple chart types showing key insights from this data.');
  }, [generateDashboard]);

  const handleDashboardInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    // Don't update state on every keystroke - only when needed
    // setDashboardInput(e.target.value);
  }, []);

  const handleChartClick = useCallback((chartData: ChatChartData) => {
    console.log('🎯 Chart clicked for focus view:', chartData.title);
    console.log('🎯 Chart data:', chartData);
    setFocusedChart(chartData);
  }, []);

  const closeFocusedChart = useCallback(() => {
    setFocusedChart(null);
  }, []);

  // Prevent dashboard input from triggering parent re-renders
  const dashboardInputRef = useRef<HTMLTextAreaElement>(null);

  // Parse dashboard response to extract multiple charts
  const parseDashboardResponse = useCallback((content: string, userPrompt?: string) => {
    console.log('🔍 Parsing dashboard response length:', content.length);
    console.log('🔍 Full response:', content);
    
    // Extract multiple chart blocks from the response
    const chartPattern = /```chart\s*([\s\S]*?)\s*```/gi;
    const charts: any[] = [];
    let match;
    let matchCount = 0;
    
    while ((match = chartPattern.exec(content)) !== null) {
      matchCount++;
      console.log(`📊 Found chart block #${matchCount}:`, match[1].substring(0, 100) + '...');
      
      try {
        const jsonStr = match[1];
        const parsed = JSON.parse(jsonStr.trim());
        console.log(`✅ Successfully parsed chart #${matchCount}:`, parsed.title);
        
        if (parsed.type && (parsed.labels || parsed.datasets || parsed.data)) {
          // Transform deprecated chart types
          if (parsed.type === 'horizontalBar') {
            parsed.type = 'bar';
            parsed.indexAxis = 'y';
            console.log('🔄 Transformed horizontalBar to bar with indexAxis: y');
          }
          
          // Transform old format if needed
          if (parsed.type === 'scatter' && parsed.data && !parsed.datasets) {
            const transformedChart = {
              type: parsed.type,
              title: parsed.title,
              description: parsed.description,
              labels: [],
              datasets: [
                {
                  label: parsed.title || 'Data Points',
                  data: parsed.data
                }
              ],
              xAxisLabel: parsed.xAxisLabel,
              yAxisLabel: parsed.yAxisLabel
            };
            charts.push(transformedChart);
          } else {
            charts.push(parsed);
          }
        }
      } catch (error) {
        console.warn(`❌ Failed to parse dashboard chart #${matchCount}:`, error);
        console.warn('❌ Raw JSON string:', match[1]);
      }
    }
    
    // Generate title from user prompt or extract from AI response
    const generateTitleFromPrompt = (prompt: string): string => {
      if (!prompt || prompt.length < 10) return 'Dynamic Dashboard';
      
      // Remove common prefixes and clean up
      let cleanPrompt = prompt
        .replace(/^(generate|create|show|display|make|build)\s+/i, '')
        .replace(/\s+(dashboard|chart|graph|visualization|report)\s+/i, ' ')
        .replace(/\s+with\s+.*/i, '')
        .replace(/\s+showing\s+.*/i, '')
        .replace(/\s+from\s+this\s+data.*/i, '')
        .trim();
      
      // Capitalize first letter and limit length
      cleanPrompt = cleanPrompt.charAt(0).toUpperCase() + cleanPrompt.slice(1);
      
      // Truncate if too long
      if (cleanPrompt.length > 50) {
        cleanPrompt = cleanPrompt.substring(0, 47) + '...';
      }
      
      return cleanPrompt || 'Dynamic Dashboard';
    };

    const title = userPrompt 
      ? generateTitleFromPrompt(userPrompt)
      : (content.match(/#{1,3}\s*([^#\n]+)/)?.[1]?.trim() || 'Dynamic Dashboard');
    
    console.log(`📊 Total chart blocks found: ${matchCount}`);
    console.log(`📊 Successfully parsed charts: ${charts.length}`);
    console.log('📊 Final charts array:', charts.map(c => ({ title: c.title, type: c.type })));
    
    return {
      title,
      charts,
      content: content.replace(/```chart[\s\S]*?```/g, '').trim()
    };
  }, []);

  // Custom HUD Chart Renderer with built-in Chart.js
  const HUDChart: React.FC<{ 
    chartData: ChatChartData | null;
    onClick?: (chartData: ChatChartData) => void;
    isClickable?: boolean;
  }> = ({ chartData, onClick, isClickable = false }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [chartInstance, setChartInstance] = useState<ChartJS | null>(null);
    const [selectedDataPoint, setSelectedDataPoint] = useState<{text: string, x: number, y: number} | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    
    // Generate Chart.js brand colors (consistent with other components)
    const generateHUDColors = (count: number) => {
      const colors = [
        'rgba(54, 162, 235, 0.8)',   // Blue
        'rgba(255, 99, 132, 0.8)',   // Pink/Red
        'rgba(255, 205, 86, 0.8)',   // Yellow
        'rgba(75, 192, 192, 0.8)',   // Teal
        'rgba(153, 102, 255, 0.8)',  // Purple
        'rgba(255, 159, 64, 0.8)',   // Orange
        'rgba(201, 203, 207, 0.8)',  // Grey
      ];
      return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
    };

    // Show interactive data point details
    const showDataPointDetails = (text: string, element: any, chart: any) => {
      const canvasRect = chart.canvas.getBoundingClientRect();
      const x = canvasRect.left + element.element.x;
      const y = canvasRect.top + element.element.y;
      
      setSelectedDataPoint({ text, x, y });
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setSelectedDataPoint(null);
      }, 5000);
    };

    // Create chart configuration
    const createChartConfig = (data: ChatChartData) => {
      if (data.type === 'scatter') {
        console.log('🔍 Creating scatter plot with data:', data);
        console.log('📊 Scatter datasets:', data.datasets);
      }
      const colors = generateHUDColors(data.datasets.length);
      
      // Detect current theme for chart styling
      const isDarkTheme = document.documentElement.getAttribute('data-theme') !== 'light';
      const textColor = isDarkTheme ? 'rgba(255, 255, 255, 0.8)' : 'rgba(15, 23, 42, 0.8)';
      const gridColor = isDarkTheme ? 'rgba(0, 255, 255, 0.2)' : 'rgba(100, 116, 139, 0.3)';
      const borderColor = isDarkTheme ? 'rgba(0, 255, 255, 0.3)' : 'rgba(100, 116, 139, 0.4)';
      
      const config: any = {
        type: data.type === 'area' ? 'line' : data.type,
        data: {
          labels: data.labels,
          datasets: data.datasets.map((dataset, index) => {
            const baseColor = colors[index];
            const brightColor = baseColor.replace('0.8', '1');
            const glowColor = baseColor.replace('0.8', '0.4');
            
            return {
              ...dataset,
              backgroundColor: data.type === 'area' || data.type === 'bar' || data.type === 'pie' || data.type === 'doughnut' 
                ? baseColor 
                : glowColor,
              borderColor: brightColor,
              pointBackgroundColor: brightColor,
              pointBorderColor: brightColor,
              pointBorderWidth: 2,
              pointRadius: data.type === 'scatter' ? 8 : (data.type === 'line' || data.type === 'area' ? 4 : 0),
              pointHoverRadius: data.type === 'scatter' ? 12 : (data.type === 'line' || data.type === 'area' ? 6 : 0),
              borderWidth: data.type === 'line' || data.type === 'area' ? 3 : data.type === 'scatter' ? 0 : 2,
              fill: data.type === 'area' ? true : false,
              tension: data.type === 'line' || data.type === 'area' ? 0.4 : undefined,
              showLine: data.type === 'scatter' ? false : true,
            };
          })
        },
        options: {
          indexAxis: data.indexAxis || 'x', // Support horizontal bar charts
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: chartData?.type === 'pie' ? 1.2 : 1.8, // More square aspect ratio to fill space
          layout: {
            padding: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0
            }
          },
          plugins: {
            // Enable the Colors plugin for automatic color assignment
            colors: {
              enabled: true,
              forceOverride: false // Allow manual colors to take precedence
            },
            title: {
              display: false // We handle title in HUD header
            },
            legend: {
              display: data.datasets.length > 1,
              position: 'top' as const,
              align: 'center' as const,
              labels: {
                color: '#ffffff',
                font: {
                  family: "'Orbitron', 'Courier New', monospace",
                  size: 12,
                  weight: 'bold'
                },
                padding: 20,
                usePointStyle: true,
                pointStyle: 'rectRounded',
                boxWidth: 12,
                boxHeight: 12,
                generateLabels: function(chart: any) {
                  const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
                  const labels = original.call(this, chart);
                  
                  labels.forEach((label: any, index: number) => {
                    label.fillStyle = colors[index];
                    label.strokeStyle = colors[index].replace('0.8', '1');
                    label.lineWidth = 2;
                  });
                  
                  return labels;
                }
              }
            },
            tooltip: {
              enabled: true,
              backgroundColor: isDarkTheme ? 'rgba(0, 15, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              titleColor: isDarkTheme ? '#00ffff' : '#1e40af',
              bodyColor: isDarkTheme ? '#ffffff' : '#1f2937',
              borderColor: isDarkTheme ? '#00ffff' : '#3b82f6',
              borderWidth: 2,
              cornerRadius: 10,
              displayColors: true,
              titleFont: {
                family: "'Orbitron', 'Courier New', monospace",
                size: 14,
                weight: 'bold'
              },
              bodyFont: {
                family: "'Orbitron', 'Courier New', monospace",
                size: 12
              },
              callbacks: {
                label: function(context: any) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  
                  const value = context.parsed.y !== undefined ? context.parsed.y : context.parsed;
                  if (typeof value === 'number') {
                    if (value >= 1000000) {
                      label += (value / 1000000).toFixed(2) + 'M';
                    } else if (value >= 1000) {
                      label += (value / 1000).toFixed(1) + 'K';
                    } else {
                      label += value.toLocaleString();
                    }
                  } else {
                    label += value;
                  }
                  
                  return label;
                }
              }
            }
          },
          scales: data.type !== 'pie' && data.type !== 'doughnut' ? {
            x: {
              title: {
                display: !!data.xAxisLabel,
                text: data.xAxisLabel?.toUpperCase() || '',
                color: textColor,
                font: {
                  family: "'Orbitron', 'Courier New', monospace",
                  size: 13,
                  weight: 'bold'
                },
                padding: { top: 10, bottom: 5 }
              },
              border: {
                display: false
              },
              grid: {
                color: gridColor,
                lineWidth: 1,
                drawBorder: false
              },
              ticks: {
                color: textColor,
                font: {
                  family: "'Orbitron', 'Courier New', monospace",
                  size: 11,
                  weight: 'normal'
                },
                padding: 8,
                maxRotation: 45,
                minRotation: 0,
                callback: function(value: any, index: number): string {
                  const label = (this as any).getLabelForValue(value);
                  // Truncate long labels
                  return label && label.length > 10 ? label.substring(0, 10) + '...' : label;
                }
              }
            },
            y: {
              title: {
                display: !!data.yAxisLabel,
                text: data.yAxisLabel?.toUpperCase() || '',
                color: textColor,
                font: {
                  family: "'Orbitron', 'Courier New', monospace",
                  size: 13,
                  weight: 'bold'
                },
                padding: { top: 5, bottom: 10 }
              },
              border: {
                display: false
              },
              grid: {
                color: gridColor,
                lineWidth: 1,
                drawBorder: false
              },
              ticks: {
                color: textColor,
                font: {
                  family: "'Orbitron', 'Courier New', monospace",
                  size: 11,
                  weight: 'normal'
                },
                padding: 8,
                callback: function(value: any): string {
                  if (typeof value === 'number') {
                    if (value >= 1000000) {
                      return (value / 1000000).toFixed(1) + 'M';
                    } else if (value >= 1000) {
                      return (value / 1000).toFixed(1) + 'K';
                    } else if (value >= 1 || value <= -1) {
                      return value.toLocaleString();
                    } else {
                      return value.toFixed(2);
                    }
                  }
                  return String(value);
                }
              }
            }
          } : undefined,
          elements: {
            point: {
              hoverBorderWidth: 3,
              hoverRadius: 8,
              hoverBorderColor: 'rgba(0, 255, 255, 0.8)'
            },
            line: {
              borderJoinStyle: 'round' as const,
              borderCapStyle: 'round' as const
            },
            bar: {
              borderSkipped: false,
              borderRadius: 4
            }
          },
          interaction: {
            intersect: false,
            mode: 'index' as const
          },
          onHover: (event: any, elements: any[]) => {
            // Change cursor to pointer when hovering over data points
            const canvas = event.native?.target;
            if (canvas) {
              canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
            }
          },
          onClick: (event: any, elements: any[], chart: any) => {
            if (elements.length > 0) {
              const element = elements[0];
              const datasetIndex = element.datasetIndex;
              const dataIndex = element.index;
              const dataset = chart.data.datasets[datasetIndex];
              const label = chart.data.labels ? chart.data.labels[dataIndex] : `Point ${dataIndex + 1}`;
              const value = dataset.data[dataIndex];
              
              // Create detailed info for clicked data point
              let detailText = '';
              if (typeof value === 'object' && 'x' in value && 'y' in value) {
                // Scatter plot point
                detailText = `**${dataset.label}**\n\n**${data.xAxisLabel || 'X'}:** ${value.x.toLocaleString()}\n**${data.yAxisLabel || 'Y'}:** ${value.y.toLocaleString()}`;
              } else {
                // Regular chart point
                detailText = `**${dataset.label}**\n\n**${label}:** ${typeof value === 'number' ? value.toLocaleString() : value}`;
              }
              
              // Show interactive popup with data point details
              showDataPointDetails(detailText, element, chart);
            }
          },
          animation: {
            duration: 1200,
            easing: 'easeInOutQuart' as const,
            delay: (context: any) => {
              return context.type === 'data' && context.mode === 'default' ? context.dataIndex * 50 : 0;
            }
          }
        }
      };
      
      return config;
    };

    // Initialize chart
    useEffect(() => {
      if (chartData && canvasRef.current && !isLoading) {
        // Destroy existing chart
        if (chartInstance) {
          chartInstance.destroy();
        }

        try {
          const config = createChartConfig(chartData);
          const newChart = new ChartJS(canvasRef.current, config);
          setChartInstance(newChart);
          console.log('📊 HUD Chart rendered successfully:', chartData.type);
        } catch (error) {
          console.error('❌ Chart rendering error:', error);
        }
      }

      return () => {
        if (chartInstance) {
          chartInstance.destroy();
        }
      };
    }, [chartData, isLoading]);

    // Handle loading state
    useEffect(() => {
      if (chartData) {
        const timer = setTimeout(() => setIsLoading(false), 800);
        return () => clearTimeout(timer);
      }
    }, [chartData]);

    if (!chartData) return null;

    // Enhanced validation - only show charts with good data
    const validateChartData = (data: ChatChartData) => {
      // Basic structure validation
      if (!data.type || !data.title || !data.datasets || data.datasets.length === 0) {
        console.log('❌ Chart validation failed: Missing basic structure');
        return false;
      }

             // Check if datasets have actual data
       const hasValidData = data.datasets.some((dataset: any) => {
         if (!dataset.data || !Array.isArray(dataset.data) || dataset.data.length === 0) {
           return false;
         }
         
         // For scatter plots, check if we have valid x,y coordinates
         if (data.type === 'scatter') {
           console.log('🔍 Validating scatter plot data:', dataset.data);
           const isValid = dataset.data.every((point: any) => 
             typeof point === 'object' && 
             point !== null && 
             'x' in point && 
             'y' in point &&
             typeof point.x === 'number' && 
             typeof point.y === 'number'
           );
           console.log('📊 Scatter plot validation result:', isValid);
           return isValid;
         }
         
         // For other charts, check if we have valid numbers
         return dataset.data.every((value: any) => 
           typeof value === 'number' && 
           !isNaN(value) && 
           isFinite(value)
         );
       });

      if (!hasValidData) {
        console.log('❌ Chart validation failed: No valid data in datasets');
        return false;
      }

      // Check if we have labels for non-scatter charts
      if (data.type !== 'scatter' && (!data.labels || data.labels.length === 0)) {
        console.log('❌ Chart validation failed: No labels provided');
        return false;
      }

             // Ensure labels match data length for consistency
       if (data.type !== 'scatter' && data.labels && data.datasets[0] && data.datasets[0].data) {
         const firstDataset = data.datasets[0] as any;
         const dataLength = Array.isArray(firstDataset.data) ? firstDataset.data.length : 0;
         if (data.labels.length !== dataLength) {
           console.log('❌ Chart validation failed: Labels and data length mismatch');
           return false;
         }
       }

      console.log('✅ Chart validation passed');
      return true;
    };

    // Only render if chart has valid data
    if (!validateChartData(chartData)) {
      console.log('🚫 Hiding chart due to invalid/empty data');
      console.log('🔍 Failed chart data:', JSON.stringify(chartData, null, 2));
      return null; // Hide the chart completely instead of showing error
    }

    return (
      <div 
        className={`hud-chart-container ${isClickable ? 'clickable' : ''}`}
        onClick={isClickable && onClick ? () => onClick(chartData) : undefined}
        style={isClickable ? { cursor: 'pointer' } : undefined}
      >
        <div className="hud-chart-header">
          <div className="chart-status-indicator">
            <div className={`status-dot ${isLoading ? 'loading' : 'active'}`}></div>
            <span className="status-text">
              {isLoading ? 'RENDERING' : 'DATA VISUALIZATION'}
            </span>
          </div>
          <div className="chart-title">{chartData.title}</div>
          <div className="chart-type-badge">{chartData.type.toUpperCase()}</div>
        </div>
        
        <div className="hud-chart-content">
          {isLoading ? (
            <div className="chart-loading">
              <div className="loading-grid"></div>
              <div className="loading-text">Initializing visualization matrix...</div>
              <div className="loading-bar">
                <div className="loading-progress"></div>
              </div>
            </div>
          ) : (
            <>
              <div className="chart-renderer" style={{ position: 'relative' }}>
                <canvas 
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: isFullscreen ? '220px' : '280px', // Smaller in fullscreen, normal size otherwise
                    background: 'rgba(0, 10, 20, 0.95)',
                    borderRadius: '8px'
                  }}
                />
                
                {/* Interactive Data Point Popup */}
                {selectedDataPoint && (
                  <div 
                    className="chart-data-popup"
                    style={{
                      position: 'fixed',
                      left: selectedDataPoint.x,
                      top: selectedDataPoint.y - 10,
                      transform: 'translate(-50%, -100%)',
                      zIndex: 1000,
                      pointerEvents: 'none'
                    }}
                    onClick={() => setSelectedDataPoint(null)}
                  >
                    <div className="popup-content">
                      <ReactMarkdown>{selectedDataPoint.text}</ReactMarkdown>
                    </div>
                    <div className="popup-arrow"></div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {chartData.description && (
          <div className="chart-description">
            <div className="description-icon">ℹ</div>
            <span>{chartData.description}</span>
          </div>
        )}
        
        <div className="hud-chart-footer">
          <div className="chart-metrics">
            <span className="metric">
              <span className="metric-label">DATA POINTS:</span>
              <span className="metric-value">
                {chartData.datasets.reduce((sum: number, dataset: any) => 
                  sum + (Array.isArray(dataset.data) ? dataset.data.length : 0), 0
                )}
              </span>
            </span>
            <span className="metric">
              <span className="metric-label">SERIES:</span>
              <span className="metric-value">{chartData.datasets.length}</span>
            </span>
          </div>
          <div className="chart-timestamp">
            {new Date().toLocaleTimeString('en-US', { 
              hour12: false, 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit' 
            })}
          </div>
        </div>
      </div>
    );
  };

  // Advanced HUD-style question interface with interactive elements
  const HUDQuestions: React.FC<{ questions: string[] }> = ({ questions }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    
         console.log('🎯 HUDQuestions component called with:', questions);
     
     if (questions.length === 0) {
       console.log('⚠️ No questions to display');
       return null;
     }
     
          console.log('✅ Rendering HUD questions interface with', questions.length, 'questions');

     // Ensure we have valid questions
     const validQuestions = questions.filter(q => q && q.trim().length > 0);
     if (validQuestions.length === 0) {
       console.log('⚠️ No valid questions after filtering');
       return null;
     }

     return (
      <div className="hud-questions-container">
        <div className="hud-questions-content">
          <div className="questions-grid">
            {validQuestions.map((question, index) => (
              <div
                key={index}
                className={`hud-question-item ${hoveredIndex === index ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleQuestionClick(question)}
              >
                <div className="question-index">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="question-content">
                  <div className="question-text">{question}</div>
                  <div className="question-action">
                    <span className="action-text">EXECUTE QUERY</span>
                    <div className="action-arrow">→</div>
                  </div>
                </div>
                <div className="question-glow"></div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="hud-questions-footer">
          <div className="footer-text">
            <span className="footer-label">INTERACTION:</span>
            <span className="footer-value">CLICK TO EXECUTE</span>
          </div>
          <div className="footer-timestamp">
            {new Date().toLocaleTimeString('en-US', { 
              hour12: false, 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>
      </div>
    );
  };

  // Load smart mode preference from sessionStorage
  useEffect(() => {
    const savedSmartMode = sessionStorage.getItem('smartModeEnabled');
    if (savedSmartMode === 'true') {
      setSmartMode(prev => ({ ...prev, enabled: true }));
    }
  }, []);

  // Add welcome message when chatbot first opens
  useEffect(() => {
    if (isVisible && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: generateMessageId(),
        conversationId: 'welcome',
        role: 'assistant',
        content: smartMode.enabled 
          ? `🧠 Hello! Smart Mode is active. I'll focus on the most relevant data for your questions.\n\n⏱️ **Note**: Smart filtering may increase response times for better data targeting.\n\nI can see you have ${currentDataCount} records loaded. What would you like to analyze?`
          : `Hello! I'm here to help you analyze your financial data. I can see you have ${currentDataCount} records loaded. What would you like to know about your data?`,
        createdAt: new Date().toISOString()
      };
      setMessages([welcomeMessage]);
    }
  }, [isVisible, currentDataCount, smartMode.enabled]);

  // Send a message
  const sendMessage = async (message: string) => {
    console.log('🚀 SEND MESSAGE STARTED:', message);
    console.log('🔍 Current conversationId state:', conversationId);
    if (!message.trim()) return;

    const userMessage: ChatMessage = {
      id: generateMessageId(),
      conversationId: conversationId || 'new',
      role: 'user',
      content: message.trim(),
      createdAt: new Date().toISOString()
    };

    console.log('👤 USER MESSAGE CREATED:', userMessage);

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setError(null);
    
    console.log('⏳ STARTING API CALL...');

    // Add loading assistant message
    const loadingMessageId = generateMessageId();
    const loadingMessage: ChatMessage = {
      id: loadingMessageId,
      conversationId: conversationId || 'new',
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      // Simple logic: if sessionStorage has conversation ID, use it. If empty, this is first message.
      const storedConversationId = sessionStorage.getItem('chatbot_conversationId');
      
      console.log('🔍 SessionStorage check:', storedConversationId ? `Found: ${storedConversationId}` : 'Empty (first message)');

      const request: SendMessageRequest = {
        message: message.trim(),
        conversationId: storedConversationId || undefined, // Use stored ID or undefined for first message
        dataFilter: {
          ...currentFilter,
          // Add the current data count as a limit to ensure we don't get more than what user sees
          limit: Math.min(currentDataCount, 500),
          // Pass smart mode flag to backend
          smartModeEnabled: smartMode.enabled
        }
      };

      console.log('📤 API REQUEST:', JSON.stringify(request, null, 2));
      console.log('🆔 CONVERSATION ID being sent to API:', request.conversationId);
      console.log('🧠 DEBUG: Smart mode state:', {
        enabled: smartMode.enabled,
        aggressiveness: smartMode.aggressiveness,
        smartModeInRequest: request.dataFilter.smartModeEnabled
      });

      const response = await conversationService.sendMessage(request);
      
      console.log('✅ API CALL SUCCESS');
      console.log('📥 RAW API RESPONSE:', JSON.stringify(response, null, 2));
      console.log('📏 RESPONSE CONTENT LENGTH:', response.content?.length);
      console.log('🕒 RESPONSE CREATEDAT:', response.createdAt);

      // Update conversation ID if this was the first message
      console.log('🔄 UPDATING CONVERSATION ID...');
      console.log('🔍 Current conversationId state before update:', conversationId);
      console.log('🔍 Response conversationId:', response.conversationId);
      
      // Save conversation ID from response (both to state and sessionStorage)
      if (response.conversationId) {
        const currentStoredId = sessionStorage.getItem('chatbot_conversationId');
        
        if (!currentStoredId) {
          // First message - save the new conversation ID
          console.log('💾 FIRST MESSAGE: Saving new conversation ID:', response.conversationId);
          setConversationId(response.conversationId);
          try {
            sessionStorage.setItem('chatbot_conversationId', response.conversationId);
            console.log('✅ Saved conversation ID to sessionStorage');
          } catch (error) {
            console.warn('Failed to save conversationId to sessionStorage:', error);
          }
        } else if (currentStoredId !== response.conversationId) {
          // This shouldn't happen in normal flow, but handle it
          console.log('⚠️ Conversation ID mismatch - updating:', currentStoredId, '->', response.conversationId);
          setConversationId(response.conversationId);
          try {
            sessionStorage.setItem('chatbot_conversationId', response.conversationId);
          } catch (error) {
            console.warn('Failed to update conversationId in sessionStorage:', error);
          }
        } else {
          // Continuing conversation - just sync state if needed
          if (conversationId !== response.conversationId) {
            setConversationId(response.conversationId);
          }
        }
      }

      // Replace loading message with actual response
      console.log('🔄 CREATING UPDATED MESSAGE...');
      const updatedMessage = {
        id: response.messageId,
        conversationId: response.conversationId,
        role: 'assistant' as const,
        content: response.content,
        isLoading: false,
        createdAt: response.createdAt
      };
      
      console.log('💬 UPDATED MESSAGE OBJECT:', JSON.stringify(updatedMessage, null, 2));
      
      console.log('🔄 UPDATING MESSAGES STATE...');
      setMessages(prev => {
        console.log('📝 PREVIOUS MESSAGES COUNT:', prev.length);
        console.log('🔍 LOOKING FOR LOADING MESSAGE ID:', loadingMessageId);
        
        const newMessages = prev.map(msg => {
          console.log('🔍 CHECKING MESSAGE:', msg.id, 'vs', loadingMessageId, '=', msg.id === loadingMessageId);
          return msg.id === loadingMessageId ? updatedMessage : msg;
        });
        
        console.log('📝 NEW MESSAGES COUNT:', newMessages.length);
        console.log('📄 LAST MESSAGE PREVIEW:', newMessages[newMessages.length - 1]?.content?.substring(0, 100));
        return newMessages;
      });
      
      console.log('✅ MESSAGE UPDATE COMPLETE');

    } catch (error: any) {
      console.error('❌ ERROR SENDING MESSAGE:', error);
      console.log('❌ ERROR DETAILS:', JSON.stringify(error, null, 2));
      
      // Determine user-friendly error message based on error type
      let userFriendlyMessage = 'Sorry, I encountered an error while processing your message. Please try again.';
      let shouldShowNewConversationButton = false;
      
      if (error.message?.includes('CONTEXT_LIMIT_EXCEEDED')) {
        if (!smartMode.enabled) {
          userFriendlyMessage = '🔄 **Conversation Too Long**\n\nOur conversation has become too long for me to process effectively due to the large dataset size.\n\n**Would you like me to enable Smart Filtering?**\n\n✨ Smart mode will automatically:\n• Focus on data relevant to your questions\n• Filter out unnecessary KPIs and time periods\n• Maintain conversation context longer\n\n⏱️ **Trade-off**: Responses may be slower but much more targeted for large datasets.';
          shouldShowNewConversationButton = true;
          // Add smart mode offer flag
          setMessages(prev => prev.map(msg => 
            msg.id === loadingMessageId 
              ? { ...msg, showSmartModeOffer: true }
              : msg
          ));
        } else {
          userFriendlyMessage = '🔄 **Dataset Too Large**\n\nEven with Smart Mode enabled, this dataset is too large to process. This can happen with very large or complex queries.\n\n**What you can do:**\n• Try a more specific question about particular aspects of your data\n• Filter your data to focus on specific time periods or locations\n• Start a new conversation for a fresh start';
          shouldShowNewConversationButton = true;
        }
      } else if (error.message?.includes('DATA_TOO_LARGE')) {
        // Extract the detailed message from the error
        const detailedMessage = error.message.replace('DATA_TOO_LARGE: ', '');
        userFriendlyMessage = `📊 **Dataset Too Large**\n\n${detailedMessage}`;
        shouldShowNewConversationButton = false; // Don't show new conversation button since filtering is the solution
      } else if (error.message?.includes('AI_VALIDATION_ERROR')) {
        userFriendlyMessage = '⚠️ **AI Processing Error**\n\nThere was a validation issue with the AI model. This usually happens when:\n• The request format is unexpected\n• The data is too complex to process\n\nPlease try rephrasing your question or starting a new conversation.';
        shouldShowNewConversationButton = true;
      } else if (error.message?.includes('input length and `max_tokens` exceed context limit')) {
        userFriendlyMessage = '📊 **Data Too Large**\n\nThe conversation and data are too large for the AI to process in one request.\n\n**Solutions:**\n• Start a new conversation for a fresh start\n• Ask more specific questions about particular aspects of your data\n• Try filtering your data to focus on specific time periods or locations\n\nThis helps me give you more focused and accurate insights!';
        shouldShowNewConversationButton = true;
      }
      
      // Replace loading message with error message
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessageId 
          ? {
              ...msg,
              content: userFriendlyMessage,
              isLoading: false,
              error: error.message,
              showNewConversationButton: shouldShowNewConversationButton
            }
          : msg
      ));
      
      setError(error.message);
    } finally {
      console.log('🏁 SEND MESSAGE FINISHED');
      setIsTyping(false);
    }
  };

  // Handle clicking on a recommended question (memoized to prevent re-renders)
  const handleQuestionClick = useCallback((question: string) => {
    // Clear any existing error
    setError(null);
    
    // Set the question in the input field
    setInputMessage(question);
    
    // Automatically send the question
    sendMessage(question);
  }, [sendMessage]);

  // Handle input key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };

  // Clear conversation
  const clearConversation = () => {
    console.log('🧹 CLEARING CONVERSATION - Current ID:', conversationId);
    setMessages([]);
    setConversationId(null);
    setError(null);
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('chatbot_conversationId');
    } catch (error) {
      console.warn('Failed to clear conversationId from sessionStorage:', error);
    }
    
    // Add welcome message for new conversation
    const welcomeMessage: ChatMessage = {
      id: generateMessageId(),
      conversationId: 'welcome',
      role: 'assistant',
      content: `🔄 New conversation started! I'm here to help you analyze your financial data. I can see you have ${currentDataCount} records loaded. What would you like to know about your data?`,
      createdAt: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
    console.log('🧹 CONVERSATION CLEARED - New conversation ID set to null');
  };

  // Format timestamp (memoized)
  const formatTime = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Memoized messages list to prevent re-rendering of historical messages
  const renderedMessages = useMemo(() => {
    console.log('🔍 DEBUG: Rendering messages, count:', messages.length);
    console.log('🔍 DEBUG: Messages array:', messages.map(m => ({ id: m.id, role: m.role, contentLength: m.content?.length, isLoading: m.isLoading })));
    
    return messages.map((message) => {
      console.log('🔍 DEBUG: Rendering message:', { id: message.id, role: message.role, contentLength: message.content?.length, isLoading: message.isLoading });
      return (
      <div 
        key={message.id} 
        className={`message ${message.role} ${message.isLoading ? 'loading' : ''} ${message.error ? 'error' : ''}`}
      >
        <div className="message-content">
          {message.isLoading ? (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          ) : (
            <>
                             {message.role === 'assistant' ? (
                 (() => {
                   const { mainContent, recommendedQuestions, chartData } = getCachedParsedResponse(message.id, message.content);
                   return (
                     <>
                                             <div className="message-text">
                        <div dangerouslySetInnerHTML={{ __html: processMarkdown(mainContent, hideCalculations) }} />
                      </div>
                       {/* Debug: Show raw content in console */}
                       {console.log('Full AI response:', message.content)}
                       {chartData && console.log('🎯 Chart data for rendering:', chartData)}
                       {console.log('🔍 Recommended questions parsed:', recommendedQuestions)}
                       {showVisualizations && <HUDChart chartData={chartData} onClick={handleChartClick} isClickable={true} />}
                       <HUDQuestions questions={recommendedQuestions} />
                     </>
                   );
                 })()
               ) : (
                <div className="message-text">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              )}
            </>
          )}
          
          {/* Show Smart Mode offer or New Conversation buttons for context limit errors */}
          {message.showSmartModeOffer && !smartMode.enabled && (
            <div className="error-action-container">
              <button 
                className="smart-mode-offer-button"
                onClick={enableSmartMode}
              >
                🧠 Enable Smart Filtering
              </button>
                            <button
                className="new-conversation-button secondary"
                onClick={() => {
                  // Clear conversation and messages
                  setConversationId(null);
                  setMessages([]);
                  setError(null);
                  // Clear sessionStorage
                  try {
                    sessionStorage.removeItem('chatbot_conversationId');
                  } catch (error) {
                    console.warn('Failed to clear conversationId from sessionStorage:', error);
                  }
                  
                  // Add welcome message for new conversation
                  const welcomeMessage: ChatMessage = {
                    id: generateMessageId(),
                    conversationId: 'welcome',
                    role: 'assistant',
                    content: `Hello! I'm here to help you analyze your financial data. I can see you have ${currentDataCount} records loaded. What would you like to know about your data?`,
                    createdAt: new Date().toISOString()
                  };
                  setMessages([welcomeMessage]);
                }}
              >
                🔄 Start New Conversation
              </button>
            </div>
          )}
          
          {/* Show only "Start New Conversation" button when smart mode is already enabled or for other errors */}
          {message.showNewConversationButton && !message.showSmartModeOffer && (
            <div className="error-action-container">
                            <button
                className="new-conversation-button"
                onClick={() => {
                  // Clear conversation and messages
                  setConversationId(null);
                  setMessages([]);
                  setError(null);
                  // Clear sessionStorage
                  try {
                    sessionStorage.removeItem('chatbot_conversationId');
                  } catch (error) {
                    console.warn('Failed to clear conversationId from sessionStorage:', error);
                  }
                  
                  // Add welcome message for new conversation
                  const welcomeMessage: ChatMessage = {
                    id: generateMessageId(),
                    conversationId: 'welcome',
                    role: 'assistant',
                    content: `Hello! I'm here to help you analyze your financial data. I can see you have ${currentDataCount} records loaded. What would you like to know about your data?`,
                    createdAt: new Date().toISOString()
                  };
                  setMessages([welcomeMessage]);
                }}
              >
                🔄 Start New Conversation
              </button>
            </div>
          )}
        </div>
        <div className="message-time">
          {formatTime(message.createdAt)}
        </div>
      </div>
      );
    });
     }, [messages, showVisualizations, hideCalculations, getCachedParsedResponse, formatTime]);

  // Dashboard Modal Component - Memoized to prevent re-renders
  const DashboardModal = useCallback(() => {
    if (!showDashboard) return null;

    return (
      <div className="dashboard-modal-overlay" onClick={(e) => {
        // Only close if clicking directly on the overlay, not on any child elements
        // Also don't close if we're currently minimizing the chatbot
        if (e.target === e.currentTarget && !isMinimizing) {
          console.log('📊 Dashboard overlay clicked - closing dashboard');
          setShowDashboard(false);
        }
      }}>
        <div className="dashboard-modal" onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}>
          <div className="dashboard-header">
            <div className="dashboard-title">
              <span className="dashboard-icon">📊</span>
              <h2>{dashboardData?.title || 'Dynamic Dashboard'}</h2>
              <div className="dashboard-status">
                {isDashboardLoading ? 'GENERATING...' : `${dashboardData?.charts?.length || 0} CHARTS`}
              </div>
            </div>
            <button 
              className="dashboard-close"
              onClick={() => setShowDashboard(false)}
              title="Close Dashboard"
            >
              ✕
            </button>
          </div>

          <div className="dashboard-content">
            {!dashboardData && !isDashboardLoading ? (
              <div className="dashboard-input-section">
                <div className="dashboard-input-header">
                  <h3>What would you like to see in your dashboard?</h3>
                  <p>Describe the insights, metrics, or visualizations you want to analyze.</p>
                </div>
                <form className="dashboard-input-container" onSubmit={(e) => {
                  console.log('📋 Form submitted');
                  e.preventDefault();
                  e.stopPropagation();
                  handleDashboardSubmit();
                }}>
                  <textarea
                    ref={dashboardInputRef}
                    className="dashboard-input"
                    defaultValue=""
                    onChange={handleDashboardInputChange}
                    placeholder="e.g., Show me sales trends by dealer, customer satisfaction correlations, top performing regions, seasonal patterns..."
                    rows={4}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDashboardSubmit();
                      }
                    }}
                    onInput={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                  />
                  <div className="dashboard-input-actions">
                    <button 
                      type="button"
                      className="dashboard-generate-btn"
                      onClick={(e) => {
                        console.log('🔥 Generate button clicked');
                        e.preventDefault();
                        e.stopPropagation();
                        handleDashboardSubmit();
                      }}
                    >
                      Generate Dashboard
                    </button>
                    <button 
                      type="button"
                      className="dashboard-default-btn" 
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('🎯 Default button clicked');
                        // Use input text if available, otherwise use default
                        const inputValue = dashboardInputRef.current?.value?.trim();
                        const message = inputValue || 'Generate a comprehensive dashboard with multiple chart types showing key insights from this data.';
                        generateDashboard(message);
                      }}
                    >
                      Use Default Analysis
                    </button>
                  </div>
                </form>
              </div>
            ) : isDashboardLoading ? (
              <div className="dashboard-loading">
                <div className="loading-spinner"></div>
                <div className="loading-text">AI is analyzing your data and creating dashboard...</div>
              </div>
            ) : dashboardData?.error ? (
              <div className="dashboard-error">
                <div className="error-icon">⚠️</div>
                <div className="error-text">{dashboardData.error}</div>
                <button onClick={handleDashboardRetry} className="retry-button">Try Again</button>
              </div>
            ) : (
              <>
                <div className="dashboard-controls">
                  <button 
                    className="new-dashboard-btn"
                    onClick={() => {
                      setDashboardData(null);
                      if (dashboardInputRef.current) {
                        dashboardInputRef.current.value = '';
                      }
                    }}
                  >
                    ✨ Create New Dashboard
                  </button>
                </div>
                <div className="dashboard-grid">
                  {dashboardData?.charts?.map((chart: any, index: number) => (
                    <div key={index} className="dashboard-chart-item">
                      <HUDChart 
                        chartData={chart} 
                        onClick={handleChartClick}
                        isClickable={true}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }, [showDashboard, dashboardData, isDashboardLoading, isMinimizing, generateDashboard, handleDashboardSubmit, handleDashboardRetry, handleDashboardInputChange]);

  // Focused Chart Modal Component
  const FocusedChartModal = useCallback(() => {
    if (!focusedChart) return null;

    return (
      <div className="chart-focus-modal-overlay" onClick={closeFocusedChart}>
        <div className="chart-focus-modal" onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}>
          <div className="chart-focus-header">
            <div className="chart-focus-title">
              <span className="chart-focus-icon">🎯</span>
              <h2>{focusedChart.title}</h2>
              <div className="chart-focus-type">{focusedChart.type.toUpperCase()}</div>
            </div>
            <button 
              className="chart-focus-close"
              onClick={closeFocusedChart}
              title="Close Focused View"
            >
              ✕
            </button>
          </div>
          <div className="chart-focus-content">
            <HUDChart chartData={focusedChart} />
          </div>
          {focusedChart.description && (
            <div className="chart-focus-description">
              {focusedChart.description}
            </div>
          )}
        </div>
      </div>
    );
  }, [focusedChart, closeFocusedChart]);

  if (!isVisible) return null;

  // If minimized, show only a floating restore button
  if (isMinimized) {
    return (
      <div className="minimized-chatbot">
        <button 
          className="restore-button"
          onClick={(e) => toggleMinimize(e)}
          title="Restore AI Assistant"
        >
          💬 AI Assistant
        </button>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`financial-chatbot ${isFullscreen ? 'fullscreen' : ''}`}
        style={isFullscreen ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999999
        } : {}}
      >
      <div className="chatbot-header">
        <div className="chatbot-title">
          <span className="chatbot-icon">💬</span>
          <h3>AI Data Assistant{isFullscreen ? ' - Fullscreen' : ''}</h3>
          <span className="data-context">
            {currentDataCount} records
            {smartMode.enabled && smartMode.dataReduction && (
              <span className="data-reduction-badge">
                • {smartMode.dataReduction.percentage}% focused
              </span>
            )}
          </span>
        </div>
        
        {/* Smart Mode Toggle */}
        <div className="smart-mode-section">
          <div className="smart-mode-toggle">
            <label 
              className="toggle-label"
              title={smartMode.enabled 
                ? "Smart Mode: Intelligently filters data for better focus (slower responses)" 
                : "Standard Mode: Uses all data (faster responses, may hit limits)"
              }
            >
              <span className={`toggle-text ${smartMode.enabled ? 'active' : ''}`}>
                {smartMode.enabled ? '🧠 Smart Mode' : '🔍 Standard Mode'}
              </span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={smartMode.enabled}
                  onChange={toggleSmartMode}
                />
                <span className="slider"></span>
              </div>
            </label>
          </div>
        </div>
        <div className="chatbot-actions">
          <button 
            className="new-conversation-button"
            onClick={clearConversation}
            title="Start a new conversation (clears chat history)"
          >
            🔄 New Chat
          </button>
          <button 
            className={`calculation-toggle ${hideCalculations ? 'active' : ''}`}
            onClick={() => setHideCalculations(!hideCalculations)}
            title={hideCalculations ? 'Show calculation details' : 'Hide calculation details'}
          >
            🔍
          </button>
          <button 
            className="dashboard-button"
            onClick={handleDashboardClick}
            title="Generate Interactive Dashboard"
          >
            📈
          </button>
          <button 
            className="minimize-button"
            onClick={(e) => toggleMinimize(e)}
            title={isMinimized ? 'Restore AI Assistant' : 'Minimize AI Assistant'}
          >
            {isMinimized ? '🔼' : '🔽'}
          </button>
          <button 
            className="fullscreen-button"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen (F11 or Ctrl+Shift+F)'}
          >
            {isFullscreen ? '⊡' : '⊞'}
          </button>
          <button 
            className={`visualization-toggle ${showVisualizations ? 'active' : 'inactive'}`}
            onClick={() => setShowVisualizations(!showVisualizations)}
            title={showVisualizations ? 'Hide visualizations' : 'Show visualizations'}
          >
            📊
          </button>
          <button 
            className="clear-button"
            onClick={clearConversation}
            title="Start a new conversation"
          >
            🔄
          </button>
          <button 
            className="close-button"
            onClick={onClose}
            title="Close chatbot"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {renderedMessages}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="chat-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="chat-input-container">
        <div className="chat-input">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask questions about your financial data..."
            disabled={isTyping}
            rows={1}
            style={{
              minHeight: '40px',
              maxHeight: '120px',
              resize: 'none'
            }}
          />
          <button 
            onClick={() => sendMessage(inputMessage)}
            disabled={!inputMessage.trim() || isTyping}
            className="send-button"
          >
            {isTyping ? '⏳' : '➤'}
          </button>
        </div>
        <div className="input-hint">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>

    <DashboardModal />
    <FocusedChartModal />
  </>
  );
};

// Export without React.memo to avoid state reset issues
export default FinancialDataChatbot;